import { Box, FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import { FormikProps, getIn } from "formik";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../components/Text";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import CbtCheckboxNoteList from "./CbtCheckboxNoteList";

// All ACT fields live nested under `psychotherapyActVisitNote`.
const ACT_NOTE_PATH = "psychotherapyActVisitNote";

// key = boolean flag, `${key}Note` = its Client Response free text.
export const ACT_INTERVENTIONS = [
  {
    key: "psychoeducationRegardingModel",
    label: "Psychoeducation regarding the ACT model",
  },
  {
    key: "mindfulnessPresentMomentAwareness",
    label: "Mindfulness / Present-moment awareness exercises",
  },
  {
    key: "cognitiveDefusionTechniques",
    label: "Cognitive defusion techniques",
  },
  {
    key: "acceptanceOfDifficultThoughts",
    label: "Acceptance of difficult thoughts and emotions",
  },
  { key: "selfAsContextExercises", label: "Self-as-context exercises" },
  { key: "valuesClarification", label: "Values clarification" },
  { key: "committedActionPlanning", label: "Committed action planning" },
  { key: "experientialExercises", label: "Experiential exercises" },
  {
    key: "metaphors",
    label: "Metaphors (e.g., Passengers on the Bus, Leaves on a Stream)",
  },
  {
    key: "behavioralCommitmentStrategies",
    label: "Behavioral commitment strategies",
  },
  { key: "relapsePrevention", label: "Relapse prevention" },
] as const;

// Progress single-select (stored on psychotherapyActVisitNote.initialProgress)
export const ACT_PROGRESS_OPTIONS = [
  // "Initial Session" removed per BA. This array drives both the form radios
  // and the summary/PDF label lookup, so removing it here covers both.
  // { value: "INITIAL_SESSION", label: "Initial Session" },
  { value: "MINIMAL_PROGRESS", label: "Minimal Progress" },
  { value: "MODERATE_PROGRESS", label: "Moderate Progress" },
  { value: "SIGNIFICANT_PROGRESS", label: "Significant Progress" },
] as const;

interface ActComponentsProps {
  formik: FormikProps<Form2Payload>;
}

const ActComponents = ({ formik }: ActComponentsProps) => (
  <Grid item xs={12}>
    <Box>
      <Text variant="subtitle1" fontWeight={600} mb={1}>
        ACT-Specific Components (Hexaflex)
      </Text>

      {/* ACT Interventions Provided */}
      <Text variant="body1" fontWeight={600} mb={0.5}>
        ACT Interventions Provided
      </Text>
      <CbtCheckboxNoteList
        formik={formik}
        items={ACT_INTERVENTIONS}
        notePath={ACT_NOTE_PATH}
        noteLabel="Client Response"
        columns={2}
        compact
      />

      {/* Progress */}
      <Text variant="body1" fontWeight={600} mt={2} mb={0.5}>
        Progress
      </Text>
      <RadioGroup
        row
        name={`${ACT_NOTE_PATH}.initialProgress`}
        value={getIn(formik.values, `${ACT_NOTE_PATH}.initialProgress`) ?? ""}
        onChange={(e) =>
          formik.setFieldValue(
            `${ACT_NOTE_PATH}.initialProgress`,
            e.target.value
          )
        }
      >
        {ACT_PROGRESS_OPTIONS.map((option) => (
          <FormControlLabel
            key={option.value}
            sx={{ my: -0.25, mr: 1 }}
            value={option.value}
            control={<Radio size="small" sx={{ py: 0.25 }} />}
            label={<Text variant="body2">{option.label}</Text>}
          />
        ))}
      </RadioGroup>
      <Flex mt={0.5} width="100%">
        <CustomTextArea
          label="Evidence"
          rows={2}
          placeholder="Enter evidence"
          value={
            getIn(formik.values, `${ACT_NOTE_PATH}.initialProgressEvidence`) ??
            ""
          }
          onChange={(e) =>
            formik.setFieldValue(
              `${ACT_NOTE_PATH}.initialProgressEvidence`,
              e.target.value
            )
          }
        />
      </Flex>
    </Box>
  </Grid>
);

export default ActComponents;
